import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GET_LENDER_INSPECTIONS, GET_MY_INSPECTION, post } from '@services';
import Button from '@components/forms/Button';
import { ReactComponent as PlusIcon } from '@assets/icons/plus_icon.svg';
import { ReactComponent as NoResult } from '@assets/icons/no_result.svg';
import { useAuthStore, useInspectionDetailStore, useInspectionStore } from '@stores';
import { parseISO } from 'date-fns';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _uniqBy from 'lodash/uniqBy';
import InspectionTable from './components/InspectionTable';
import FilterBar from './components/FilterBar';
import Loader from '@components/widgets/Loader';
import ErrorDisplay from '@components/widgets/ErrorDisplay';
import { formatName } from '@utils/formatters';
import { getSelectedLender, hasActiveLenderBrokers, hasActiveLenders } from '@utils/userUtils';
const Inspection = () => {
  const navigate = useNavigate();
  const [lenderInspections, setLenderInspections] = useState<any[]>([]);
  const [lenderInspectionsOriginal, setLenderInspectionsOriginal] = useState<
    any[]
  >([]);
  const { query, status, asset, lender, setLenderOptions } =
    useInspectionStore();
  const { reset } = useInspectionDetailStore();
  const { selectedOrganisationId, isInspectionEnabled } = useAuthStore();
  const [isLoading, setLoading] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadMore = (createdTime: string) => {
    // Always use the createTime passed from the table (last visible item)
    fetchData(createdTime);
  };
  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchData();
  }, []);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    fetchData();
  };

  const handleContactSupport = () => {
    // Navigate to support page
    window.open(
      'https://www.authentik8.com.au/support-centre-contact-us',
      '_blank'
    );
  };
  function filterInspections(
    keyword: string,
    status: string,
    asset: string,
    lender: string
  ) {
    let newData = lenderInspectionsOriginal;
    const lenders = _uniqBy(
      _map(lenderInspectionsOriginal, (item) =>
        item.apiClient
          ? {
              label: item.apiClient?.name,
              value: item.apiClient?.clientId
            }
          : item.lender
            ? {
                label: formatName(
                  item.lender?.firstName,
                  item.lender?.lastName
                ),
                value: item.lender?.userId
              }
            : item.broker
              ? {
                  label: formatName(
                    item.broker?.firstName,
                    item.broker?.lastName
                  ),
                  value: item.broker?.userId
                }
              : null
      ).filter(Boolean),
      'value'
    );
    // Sort alphabetically by label (name)
    lenders.sort((a, b) => a.label.localeCompare(b.label));
    lenders.unshift({ value: 'ALL', label: 'All' });
    setLenderOptions(lenders);
    const pattern = /[`~!@#$^&*()={}':;',\\]/g;
    keyword = keyword.replace(pattern, '');
    if (!_isNil(keyword) && keyword !== '') {
      setIsFilter(true);
      const pattern = new RegExp(keyword, 'i');
      newData = lenderInspectionsOriginal.filter((item: any) => {
        return (
          pattern.test(item.vehicleInfo) ||
          pattern.test(item.status) ||
          pattern.test(item.lenderReference) ||
          pattern.test(item.friendlyId) ||
          pattern.test(
            formatName(item.buyer?.firstName, item.buyer?.lastName)
          ) || // Check for null buyer
          pattern.test(
            formatName(item.seller?.firstName, item.seller?.lastName)
          ) || // Check for null seller
          pattern.test(
            formatName(item.lender?.firstName, item.lender?.lastName)
          ) || // Check for null lender
          pattern.test(
            formatName(item.broker?.firstName, item.broker?.lastName)
          ) || // Check for null broker
          pattern.test(item.assetType)
        );
      });
    }
    if (status !== 'ALL' && newData.length > 0) {
      setIsFilter(true);
      newData = newData.filter(
        (item: any) => item.status.toLowerCase() === status.toLowerCase()
      );
    }
    if (asset !== 'ALL' && newData.length > 0) {
      setIsFilter(true);
      newData = newData.filter(
        (item: any) => item.assetType.toLowerCase() === asset.toLowerCase()
      );
    }
    if (lender !== 'ALL' && newData.length > 0) {
      setIsFilter(true);
      newData = newData.filter(
        (item: any) =>
          item.apiClient?.clientId === lender ||
          item.lender?.userId === lender ||
          item.broker?.userId === lender
      );
    }
    //to avoid duplicate data when the length of total is just match % 40 === 0 (40 80 160)
    if (
      lenderInspections.length > 0 &&
      newData.length > 0 &&
      parseISO(newData[0].createdTime) >=
        parseISO(lenderInspections[lenderInspections.length - 1].createdTime)
    ) {
      setIsFilter(false);
    }
    setLenderInspections(newData);
  }
  const fetchData = async (createdTime?: string) => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      const organisationId = selectedOrganisationId;

      const params: { lenderOrganisationId?: string; olderThan?: string } =
        {};
      if (createdTime) {
        params.olderThan = createdTime;
      }

      const hasActiveLenderBrokersResult = await hasActiveLenderBrokers();
      const hasActiveLendersResult = await hasActiveLenders();

      // A user who is not an active lender must not call listLenderInspections.
      // listBrokerInspections is not used here; their own list is listMyInspections.
      const brokerOnlyUser =
        hasActiveLenderBrokersResult && !hasActiveLendersResult;
      const lenderAndBroker =
        hasActiveLendersResult && hasActiveLenderBrokersResult;

      if (brokerOnlyUser || lenderAndBroker) {
          // If has brokers, fetch both lender and broker inspections
          const lenderParams = {
            ...params,
            lenderOrganisationId: organisationId
          };

          const fetchBrokerDataRecursively = async (
            brokerParams: any,
            allBrokerInspections: any[] = []
          ): Promise<any[]> => {
            const brokerData = await post(GET_MY_INSPECTION, brokerParams);
            const newBrokerInspections = brokerData.inspections || [];
            const combinedBrokerInspections = [
              ...allBrokerInspections,
              ...newBrokerInspections
            ];

            // If we got less than 20 items, we've reached the end
            if (newBrokerInspections.length < 20) {
              return combinedBrokerInspections;
            }

            // If this is initial load and we have less than 40 total items, fetch more
            if (!createdTime && combinedBrokerInspections.length < 40) {
              const lastItem =
                newBrokerInspections[newBrokerInspections.length - 1];
              const nextBrokerParams = {
                ...brokerParams,
                olderThan: lastItem.createdTime
              };
              return fetchBrokerDataRecursively(
                nextBrokerParams,
                combinedBrokerInspections
              );
            }

            return combinedBrokerInspections;
          };

          const promiseAll = [];
          if (hasActiveLenderBrokersResult) {
            promiseAll.push(fetchBrokerDataRecursively({ ...params }));
          }
          if (lenderAndBroker) {
            promiseAll.push(post(GET_LENDER_INSPECTIONS, lenderParams));
          }
          Promise.all(promiseAll)
            .then((results) => {
              setLoading(false);

              // Broker fetch returns an inspection array; lender API returns
              // { inspections: [...] }. Normalize both before merging.
              const allInspections = results
                .flatMap((result) =>
                  Array.isArray(result) ? result : result?.inspections || []
                )
                .sort(
                  (a, b) =>
                    parseISO(b.createdTime).getTime() -
                    parseISO(a.createdTime).getTime()
                );
              setLenderInspectionsOriginal(allInspections);
              filterInspections(query, status, asset, lender);
            })
            .catch((err) => {
              setLoading(false);
              setError('Something went wrong while loading your inspections.');
              console.error('Error fetching inspections:', err);
            });
        } else {
          const url = hasActiveLendersResult
            ? GET_LENDER_INSPECTIONS
            : GET_MY_INSPECTION;
          if (hasActiveLendersResult) {
            params.lenderOrganisationId = organisationId;
          }

          post(url, params)
            .then((data) => {
              setLoading(false);
              if (data.inspections) {
                // Merge with existing data and sort to maintain proper order
                const mergedData = lenderInspectionsOriginal.concat(
                  data.inspections
                );
                const sortedMergedData = mergedData.sort(
                  (a, b) =>
                    parseISO(b.createdTime).getTime() -
                    parseISO(a.createdTime).getTime()
                );
                setLenderInspectionsOriginal(sortedMergedData);
                filterInspections(query, status, asset, lender);
              }
            })
            .catch((err) => {
              setLoading(false);
              setError('Something went wrong while loading your inspections.');
              console.error('Error fetching inspections:', err);
            });
        }
      }
  };
  useEffect(() => {
    filterInspections(query, status, asset, lender);
  }, [query, status, asset, lender, lenderInspectionsOriginal]);
  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorDisplay
        title="We couldn't load your inspections"
        message="Something went wrong while loading your inspections."
        onRetry={handleRetry}
        onContactSupport={handleContactSupport}
      />
    );
  }
  if (!isLoading && !isFilter && lenderInspections.length === 0)
    return (
      <div className="p-8 rounded-lg border border-grey-200 bg-grey-50 flex flex-row justify-center items-center m-8 no-result">
        <div className="flex flex-col gap-8 justify-center items-center">
          <div className="border border-grey-200 rounded-xl w-14 h-14 flex justify-center items-center">
            <NoResult />
          </div>
          <p className="text-lg font-semibold">No inspections found</p>
          {isInspectionEnabled() && (
            <Button
              className="capitalize bg-primary flex items-center gap-2 w-auto text-sm font-semibold py-2.5 text-white mb-1"
              onClick={() => {
                reset();
                navigate('/inspections/create');
              }}>
              <PlusIcon />
              New inspection
            </Button>
          )}
        </div>
      </div>
    );
  return (
    <>
      <div className="flex items-center justify-between lg:flex-row md:flex-col md:items-start md:w-full">
        <FilterBar />
      </div>
      <div className="pt-6">
        <InspectionTable data={lenderInspections} loadMoreData={loadMore} />
      </div>
    </>
  );
};

export default Inspection;
